import * as React from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';

interface ContainerTypeTableColumn {
  key: string;
  label: string;
  width?: string; // e.g., '150px', '88px', or 'flex-1'
  className?: string;
  headerContent?: React.ReactNode; // Custom header content (e.g., with icon)
}

interface ContainerTypeTableRow {
  id: string | number;
  cells: React.ReactNode[];
  className?: string;
  rowHeight?: string; // e.g., '60px', '76px'
  cardTitle?: React.ReactNode; // Content to display as card title on mobile (replaces first column)
}

interface ContainerTypeTableProps {
  columns: ContainerTypeTableColumn[];
  rows: ContainerTypeTableRow[];
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
  headerHeight?: string; // e.g., '54px'
}

export function ContainerTypeTable({
  columns,
  rows,
  isLoading = false,
  emptyMessage = 'No data available',
  className,
  headerHeight = '54px',
}: ContainerTypeTableProps) {
  const isMobile = useIsMobile();

  // Desktop table view
  const renderTable = () => (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:items-start tw:relative tw:w-full',
        className,
      )}
    >
      {/* Table Header */}
      <div
        className="tw:flex tw:items-center tw:relative tw:w-full tw:border-b tw:border-neutral-200 tw:dark:border-neutral-700"
        style={{ height: headerHeight }}
      >
        {columns.map((column) => (
          <div
            key={column.key}
            className={cn(
              'tw:bg-white tw:dark:bg-card tw:flex tw:gap-2 tw:h-full tw:items-center tw:px-2 tw:py-1.5 tw:relative tw:shrink-0',
              column.width === 'flex-1' || !column.width ? 'tw:flex-1' : '',
              column.className,
            )}
            style={
              column.width && column.width !== 'flex-1'
                ? { width: column.width }
                : undefined
            }
          >
            {column.headerContent || (
              <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                {column.label}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Table Rows */}
      {isLoading ? (
        <div className="tw:p-4 tw:text-center tw:text-neutral-500 tw:dark:text-neutral-400 tw:w-full">
          {i18n.t('Common:loading')}
        </div>
      ) : rows.length === 0 ? (
        <div className="tw:p-4 tw:text-center tw:text-neutral-500 tw:dark:text-neutral-400 tw:w-full">
          {emptyMessage}
        </div>
      ) : (
        rows.map((row) => {
          const rowHeight = row.rowHeight || '60px';
          return (
            <div
              key={row.id}
              className={cn(
                'tw:flex tw:items-start tw:relative tw:w-full tw:border-b tw:border-neutral-200 tw:dark:border-neutral-700 tw:last:border-b-0',
                row.className,
              )}
              style={{ height: rowHeight }}
            >
              {columns.map((column, colIndex) => (
                <div
                  key={`${row.id}-${column.key}`}
                  className={cn(
                    'tw:bg-white tw:dark:bg-card tw:flex tw:gap-1 tw:h-full tw:items-center tw:px-2 tw:py-0.5 tw:relative tw:shrink-0',
                    column.width === 'flex-1' || !column.width
                      ? 'tw:flex-1'
                      : '',
                    column.className,
                  )}
                  style={
                    column.width && column.width !== 'flex-1'
                      ? { width: column.width }
                      : undefined
                  }
                >
                  {row.cells[colIndex]}
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );

  // Mobile card view
  const renderCards = () => {
    if (isLoading) {
      return (
        <div
          className={cn(
            'tw:p-4 tw:text-center tw:text-neutral-500 tw:dark:text-neutral-400 tw:w-full',
            className,
          )}
        >
          {i18n.t('Common:loading')}
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div
          className={cn(
            'tw:p-4 tw:text-center tw:text-neutral-500 tw:dark:text-neutral-400 tw:w-full',
            className,
          )}
        >
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className={cn('tw:flex tw:flex-col tw:gap-0 tw:w-full', className)}>
        {rows.map((row, rowIndex) => {
          // Skip the first column (index 0) as it becomes the cardTitle
          const dataColumns = columns.slice(1);
          const dataCells = row.cells.slice(1);

          return (
            <div
              key={row.id}
              className={cn(
                'tw:bg-white tw:dark:bg-card tw:flex tw:flex-col tw:relative tw:w-full tw:border-b tw:border-neutral-200 tw:dark:border-neutral-700 tw:last:border-b-0',
                row.className,
              )}
            >
              {/* Card Title Row - shows "Type" label and cardTitle content */}
              <div className="tw:flex tw:items-center tw:relative tw:w-full">
                <div className="tw:bg-white tw:dark:bg-card tw:flex tw:gap-2 tw:h-full tw:items-center tw:px-2 tw:py-1.5 tw:relative tw:shrink-0 tw:w-[88px]">
                  <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                    {columns[0]?.label || i18n.t('Common:type')}
                  </span>
                </div>
                <div className="tw:flex tw:gap-2 tw:items-center tw:px-2 tw:py-1.5 tw:relative tw:flex-1 tw:min-w-0">
                  {row.cardTitle || row.cells[0]}
                </div>
              </div>

              {/* Data Rows - label-value pairs */}
              {dataColumns.map((column, colIndex) => (
                <div
                  key={`${row.id}-${column.key}`}
                  className="tw:flex tw:items-center tw:relative tw:w-full"
                >
                  <div className="tw:bg-white tw:dark:bg-card tw:flex tw:gap-2 tw:h-full tw:items-center tw:px-2 tw:pt-1.5 tw:pb-0 tw:relative tw:shrink-0 tw:w-[88px]">
                    <span className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:dark:text-neutral-100 tw:tracking-[0.07px]">
                      {column.label}
                    </span>
                  </div>
                  <div className="tw:flex tw:gap-1 tw:h-full tw:items-center tw:px-2 tw:pt-1.5 tw:pb-0 tw:relative tw:flex-1">
                    {dataCells[colIndex]}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return isMobile ? renderCards() : renderTable();
}
